import type { OrderRecord } from "@/api/types";

/** What an order did, in the user's words rather than the exchange's side + position side. */
export function orderAction(o: Pick<OrderRecord, "side" | "position_side">): { label: string; long: boolean; opening: boolean } {
  const buy = o.side === "BUY";
  const pos = (o.position_side || "").toUpperCase();
  if (pos === "LONG") return { label: buy ? "Open long" : "Close long", long: true, opening: buy };
  if (pos === "SHORT") return { label: buy ? "Close short" : "Open short", long: false, opening: !buy };
  // One-way mode or spot: the exchange only says buy or sell.
  return { label: buy ? "Buy" : "Sell", long: buy, opening: buy };
}

/** The script's order tag as a reason: btTP → Take profit. Unknown tags are shown as written. */
export function orderReason(comment: string | null | undefined): string {
  const raw = (comment || "").trim();
  if (!raw) return "";
  const tag = raw.replace(/^bt[\s_-]?/i, "").toLowerCase().replace(/[\s_-]+/g, "");
  if (/^(tp\d*|takeprofit\d*)$/.test(tag)) return "Take profit";
  if (/^(sl\d*|stop|stoploss\d*)$/.test(tag)) return "Stop loss";
  if (/^(long|short|entry|buy|sell|open)$/.test(tag)) return "Entry";
  if (/^(close|exit|closelong|closeshort)$/.test(tag)) return "Exit";
  return raw;
}

const ERRORS: [RegExp, string][] = [
  [/-2019\b|margin is insufficient/i, "Margin is insufficient"],
  [/-2022\b|reduce-?only/i, "Reduce-only rejected: no position to close"],
  [/-4164\b|min(imum)? notional|below the minimum/i, "Order below the minimum size"],
  [/-2015\b|invalid api-?key|permissions for action/i, "API key rejected (key, IP or permissions)"],
  [/-1021\b|timestamp for this request/i, "Clock out of sync with the exchange"],
  [/-1111\b|precision is over/i, "Quantity or price has too many decimals"],
  [/-4003\b|quantity less than (or equal to )?zero/i, "Quantity is zero"],
  [/insufficient balance|-2010\b/i, "Insufficient balance"],
  [/-1003\b|too many requests|rate limit/i, "The exchange is rate limiting requests"],
];

/** An exchange error in plain words; the original stays available for the tooltip. */
export function humanError(message: string | null | undefined): string {
  const raw = (message || "").trim();
  if (!raw) return "";
  for (const [re, text] of ERRORS) if (re.test(raw)) return text;
  // "futures order error -1234: Something went wrong." → "Something went wrong"
  return raw.replace(/^[a-z ]*error\s*(-?\d+)?\s*:\s*/i, "").replace(/\.$/, "") || raw;
}

/** A pending order this old never got an answer from the exchange. */
export const STALE_PENDING_MS = 60 * 60 * 1000;

export function pendingAge(o: Pick<OrderRecord, "status" | "created_at">, now = Date.now()): string | null {
  if (o.status !== "pending") return null;
  const ms = now - Date.parse(o.created_at);
  if (!Number.isFinite(ms) || ms < STALE_PENDING_MS) return null;
  const hours = Math.floor(ms / 3_600_000);
  return hours >= 24 ? `${Math.floor(hours / 24)}d` : `${hours}h`;
}
